import {Action, ActionReducer, createReducer, on} from '@ngrx/store';
import {StockItem} from '../../../shared-utilities/models-old/datastructures';
import {IPaginationData} from '../../../shared/shared-models/pagination/pagination-data';
import * as ShelfTalkerActions from './shelf-talkers.actions';
import {IFacetCounts} from '../../../shared/shared-models/type-sense/type-sense-types';
import {ISearchableFields} from '../../../shared/shared-models/type-sense/default-searchable-fields';
import {
  IFirebaseLabelDesignsData,
} from '../../../shared/shared-components/components/shared-label-designer/models/label-interfaces';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {printedShelfTalkerIds, shelfTalkerItemIds} from '../utils/shelf-talker-print-lists.utils';

export interface ShelfTalkersState {
  errors: IGlobalError[];
  exceptions: Record<string, StockItem[]>,
  isExceptionsLoading: boolean;
  isLayoutsLoading: boolean;
  isLoading: boolean;
  isRemoveTagsLoading: boolean
  isStockItemsLoading: boolean;
  layoutsLoadFailed: boolean;
  otherStockItems: Record<string, StockItem[]>,
  otherStockItemsFacetCounts: Record<string, IFacetCounts>,
  otherStockItemsFilters: Record<string, Record<string, ISearchableFields>>,
  otherStockItemsPaginationData: Record<string, IPaginationData>,
  paginationData: Record<string, IPaginationData>,
  removedExceptions: Record<string, Record<string, StockItem>>,
  removedStockItems: Record<string, Record<string, StockItem>>,
  shelfTalkerSettings: Record<string, IFirebaseLabelDesignsData>,
  sortMethod: Record<string, string>,
  stockItems: Record<string, StockItem[]>,
}

export const initialShelfTalkersState: ShelfTalkersState = {
  // Initial --------------------------------
  errors: [],
  // ShelfTalkers ------------------------------
  isExceptionsLoading: false,
  isLayoutsLoading: false,
  isLoading: false,
  isRemoveTagsLoading: false,
  isStockItemsLoading: false,
  layoutsLoadFailed: false,
  stockItems: {},
  exceptions: {},
  otherStockItems: {},
  removedStockItems: {},
  removedExceptions: {},
  paginationData: {},
  otherStockItemsPaginationData: {},
  otherStockItemsFacetCounts: {},
  otherStockItemsFilters: {},
  sortMethod: {},
  shelfTalkerSettings: {},
};

const createShelfTalkersReducer: ActionReducer<ShelfTalkersState> = createReducer(
  initialShelfTalkersState,
  on(ShelfTalkerActions.clearShelfTalkerStockItems, (state: ShelfTalkersState) => ({
    ...state,
    stockItems: {},
    exceptions: {},
  })), on(ShelfTalkerActions.getShelfTalkerStockItems, (state: ShelfTalkersState) => ({
    ...state,
    isStockItemsLoading: true,
  })),
  on(ShelfTalkerActions.getShelfTalkerStockItemsSuccess, (state: ShelfTalkersState, {
    stockItems,
    store,
  }) => ({
    ...state,
    isStockItemsLoading: false,
    stockItems: {
      ...state.stockItems,
      [store.storeId]: stockItems,
    },
  })),
  on(ShelfTalkerActions.getShelfTalkerStockItemsFailure, (state: ShelfTalkersState, {error, store}) => ({
    ...state,
    isStockItemsLoading: false,
    errors: [...state.errors, error],
  })),
  on(ShelfTalkerActions.getShelfTalkersStockItemsExceptions, (state: ShelfTalkersState) => ({
    ...state,
    isExceptionsLoading: true,
  })),
  on(ShelfTalkerActions.getShelfTalkersStockItemsExceptionsSuccess, (state: ShelfTalkersState, {
    stockItems,
    store,
  }) => ({
    ...state,
    isExceptionsLoading: false,
    exceptions: {
      ...state.stockItems,
      [store.storeId]: stockItems,
    },
  })),
  on(ShelfTalkerActions.getShelfTalkersStockItemsExceptionsFailure, (state: ShelfTalkersState, {error, store}) => ({
    ...state,
    isExceptionsLoading: false,
    errors: [...state.errors, error],
  })),
  on(ShelfTalkerActions.getOtherShelfTalkerStockItemsSuccess, (state: ShelfTalkersState, {
    stockItems,
    paginationData,
    store,
    facetCounts,
    filter,
  }) => ({
    ...state,
    isLoading: false,
    otherStockItems: {
      ...state.stockItems,
      [store.storeId]: stockItems,
    },
    otherStockItemsPaginationData: {
      ...state.paginationData,
      [store.storeId]: paginationData,
    },
    otherStockItemsFacetCounts: {
      ...state.otherStockItemsFacetCounts,
      [store.storeId]: facetCounts,
    },
    otherStockItemsFilters: {
      ...state.otherStockItemsFilters,
      [store.storeId]: filter,
    },
  })),
  on(ShelfTalkerActions.getOtherShelfTalkerStockItemsFailure, (state: ShelfTalkersState, {error, store}) => ({
    ...state,
    isLoading: false,
    errors: [...state.errors, error],
  })),
  on(ShelfTalkerActions.removeShelfTalkerTag, (state: ShelfTalkersState) => ({
    ...state,
    isRemoveTagsLoading: true,
  })),
  on(ShelfTalkerActions.removeShelfTalkerTagSuccess, (state: ShelfTalkersState, {removedStockItems, store}) => {
    const removedIds = printedShelfTalkerIds(removedStockItems);
    return ({
    ...state,
    isRemoveTagsLoading: false,
    stockItems: {
      ...state.stockItems,
      [store.storeId]: state.stockItems[store.storeId]?.filter(
        (item: StockItem) => !shelfTalkerItemIds(item).some((id: string) => removedIds.has(id)),
      ) || [],
    },
    removedStockItems: {
      ...state.removedStockItems,
      [store.storeId]: {
        ...state.removedStockItems[store.storeId],
        ...removedStockItems,
      },
    },
    });
  }),
  on(ShelfTalkerActions.removeShelfTalkerTagFailure, (state: ShelfTalkersState, {error, store}) => ({
    ...state,
    isRemoveTagsLoading: false,
    errors: [...state.errors, error],
    stockItems: {
      ...state.stockItems,
      [store.storeId]: [...(state.stockItems[store.storeId] ?? [])],
    },
  })),
  on(ShelfTalkerActions.removeShelfTalkerExceptionsTag, (state: ShelfTalkersState) => ({
    ...state,
    isRemoveTagsLoading: true,
  })),
  on(ShelfTalkerActions.removeShelfTalkerExceptionsTagSuccess, (state: ShelfTalkersState, {
    removedStockItems,
    store,
  }) => ({
    ...state,
    isRemoveTagsLoading: false,
    exceptions: {
      ...state.exceptions,
      [store.storeId]: state.exceptions[store.storeId]?.filter(
        item => !Object.keys(removedStockItems).includes(item.code),
      ) || [],
    },
    removedExceptions: {
      ...state.removedExceptions,
      [store.storeId]: {
        ...state.removedExceptions[store.storeId],
        ...removedStockItems,
      },
    },
  })),
  on(ShelfTalkerActions.removeShelfTalkerExceptionsTagFailure, (state: ShelfTalkersState, {error, store}) => ({
    ...state,
    isRemoveTagsLoading: false,
    errors: [...state.errors, error],
  })),
  on(ShelfTalkerActions.setShelfTalkersSortMethodSuccess, (state: ShelfTalkersState, {sortMethod, store}) => ({
    ...state,
    sortMethod: {
      ...state.sortMethod,
      [store.storeId]: sortMethod,
    },
  })),
  on(ShelfTalkerActions.setShelfTalkersSortMethodFailure, (state: ShelfTalkersState, {error, store}) => ({
    ...state,
    errors: [...state.errors, error],
  })),
  on(ShelfTalkerActions.getTypesenseShelfTalkersSearchResultsWithStoreId, (state: ShelfTalkersState, {
    paginationData,
    store,
  }) => ({
    ...state,
    paginationData: {
      ...state.paginationData,
      [store.storeId]: paginationData,
    },
  })),
  on(ShelfTalkerActions.getOtherItemsTypesenseShelfTalkersSearchResultsWithStoreId, (state: ShelfTalkersState, {
    paginationData,
    store,
  }) => ({
    ...state,
    otherStockItemsPaginationData: {
      ...state.otherStockItemsPaginationData,
      [store.storeId]: paginationData,
    },
  })),
  on(ShelfTalkerActions.setPageSizeForShelfTalkersAtStoreId, (state: ShelfTalkersState, {pageSize, store}) => ({
    ...state,
    paginationData: {
      ...state.paginationData,
      [store.storeId]: {
        ...state.paginationData[store.storeId],
        pageSize,
      },
    },
  })),
  on(ShelfTalkerActions.setOtherPageSizeForShelfTalkersAtStoreId, (state: ShelfTalkersState, {
    paginationData,
    store,
  }) => ({
    ...state,
    otherStockItemsPaginationData: {
      ...state.otherStockItemsPaginationData,
      [store.storeId]: paginationData,
    },
  })),
  on(ShelfTalkerActions.getShelfTalkerSettings, (state: ShelfTalkersState) => ({
    ...state,
    isLayoutsLoading: true,
    layoutsLoadFailed: false,
  })),
  on(ShelfTalkerActions.getShelfTalkerSettingsSuccess, (state: ShelfTalkersState, {
    shelfTalkerSettings,
    store,
  }) => ({
    ...state,
    isLayoutsLoading: false,
    layoutsLoadFailed: false,
    shelfTalkerSettings: {
      ...state.shelfTalkerSettings,
      [store.storeId]: shelfTalkerSettings,
    },
  })),
  on(ShelfTalkerActions.getShelfTalkerSettingsFailure, (state: ShelfTalkersState, {
    error,
    store,
  }) => ({
    ...state,
    isLayoutsLoading: false,
    layoutsLoadFailed: true,
    errors: [
      ...state.errors,
      error,
    ],
  })),
  // ====================================================================================================
  //
  // ====================================================================================================
);

export const shelfTalkersReducer = (state: ShelfTalkersState, action: Action): ShelfTalkersState => createShelfTalkersReducer(state, action);
